/**
 * Canonical site origin without trailing slash (production: https://lucentiasocial.com).
 * Improves canonical URLs and Open Graph links when the deployed host differs from
 * your primary domain. Optional: falls back to `window.location.origin` in the browser.
 */
export const SITE_ORIGIN = validOrigin(import.meta.env.VITE_SITE_URL);

/* Only a real http(s) origin counts: placeholders such as the “[SENSITIVE]”
   that `vercel env pull` writes must never end up in canonical URLs. */
function validOrigin(raw: string | undefined): string {
  try {
    const url = new URL((raw ?? "").trim());
    return /^https?:$/.test(url.protocol) ? url.origin : "";
  } catch {
    return "";
  }
}

/**
 * WhatsApp +34 604 445 729. The standard wa.me/<phone> format is the only one
 * that supports `?text=` (the Business short link `/message/…` ignores it).
 */
const WHATSAPP_URL = "https://wa.me/34604445729";

/** External links used across the landing page. */
export const SOCIAL = {
  whatsapp: WHATSAPP_URL,
  whatsappDirect: WHATSAPP_URL,
  instagram:
    "https://www.instagram.com/lucentiasocial?igsh=MWx2aTQxd2c3ZXV4Zw==",
  tiktok: "https://tiktok.com/@jeniroelstraete",
  /** Calendar for the Asesoría Estratégica. The free audit is requested on WhatsApp. */
  calendlyAudit: "https://calendly.com/lucentiasocial/asesoria-estrategica",
} as const;

/** Lead-magnet scorecard: "¿Tu Instagram llena tu agenda?". */
export const QUIZ_PATH = "/test-instagram";

/** Instagram bio link hub: share it with ?utm_source=instagram&utm_medium=bio. */
export const LINK_HUB_PATH = "/link";

/** On-site intake that must be completed before the advisory calendar opens. */
export const ADVISORY_FORM_PATH = "/asesoria-estrategica";

/**
 * Public Google Form that already collects these answers.
 * Field ids match that form so a submission lands in the same spreadsheet.
 */
export const ADVISORY_FORM_RESPONSE_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSf2dkV5y9Hny5--LTltJOCoDhkGdtg-nAyzU52CLNyCxGq_Jw/formResponse";

/**
 * Contact form POST URL. Uses Vercel `/api/contact` + Brevo by default.
 * Set `VITE_CONTACT_API_URL` when running `vite` locally against a deployed API (e.g. preview URL).
 */
export const CONTACT_FORM_ENDPOINT =
  import.meta.env.VITE_CONTACT_API_URL?.replace(/\/$/, "") ?? "/api/contact";

/** Fire-and-forget event tracking endpoint. */
export const TRACK_API_URL =
  import.meta.env.VITE_TRACK_API_URL?.replace(/\/$/, "") ?? "/api/track";

/** Admin-protected analytics dashboard data endpoint. */
export const ANALYTICS_API_URL =
  import.meta.env.VITE_ANALYTICS_API_URL?.replace(/\/$/, "") ??
  "/api/analytics";

/** Admin login endpoint. */
export const ADMIN_LOGIN_API_URL =
  import.meta.env.VITE_ADMIN_LOGIN_API_URL?.replace(/\/$/, "") ??
  "/api/admin/login";

/** Admin resources CRUD endpoint. */
export const ADMIN_RESOURCES_API_URL =
  import.meta.env.VITE_ADMIN_RESOURCES_API_URL?.replace(/\/$/, "") ??
  "/api/admin/resources";

/** Admin blog articles CRUD endpoint. */
export const ADMIN_BLOG_API_URL =
  import.meta.env.VITE_ADMIN_BLOG_API_URL?.replace(/\/$/, "") ?? "/api/admin/blog";

/** Admin signed upload URL endpoint (Supabase Storage). */
export const ADMIN_UPLOAD_URL_API =
  import.meta.env.VITE_ADMIN_UPLOAD_URL_API?.replace(/\/$/, "") ??
  "/api/admin/upload-url";

/** Admin-protected contact leads list endpoint. */
export const ADMIN_LEADS_API_URL =
  import.meta.env.VITE_ADMIN_LEADS_API_URL?.replace(/\/$/, "") ??
  "/api/admin/leads";

/** Public resources list endpoint. */
export const RESOURCES_API_URL =
  import.meta.env.VITE_RESOURCES_API_URL?.replace(/\/$/, "") ??
  "/api/resources";

/** Store endpoints: checkout, order status, purchases email (one function, see api/store). */
export const STORE_API_URL =
  import.meta.env.VITE_STORE_API_URL?.replace(/\/$/, "") ?? "/api/store";

/** Public announcement marquee. */
export const BANNER_API_URL =
  import.meta.env.VITE_BANNER_API_URL?.replace(/\/$/, "") ?? "/api/banner";

/** Admin announcement marquee editor. */
export const ADMIN_BANNER_API_URL =
  import.meta.env.VITE_ADMIN_BANNER_API_URL?.replace(/\/$/, "") ??
  "/api/admin/banner";

/** Website AI assistant (Gemini, Groq fallback). */
export const CHAT_API_URL =
  import.meta.env.VITE_CHAT_API_URL?.replace(/\/$/, "") ?? "/api/chat";

/** Admin-protected assistant conversations (lead qualification). */
export const ADMIN_ASSISTANT_API_URL =
  import.meta.env.VITE_ADMIN_ASSISTANT_API_URL?.replace(/\/$/, "") ??
  "/api/admin/assistant";
